import React, { ChangeEvent, useState } from "react";
import { AutoImportNotification, NotificationFailureType, NotificationType, NotificationTypeOption} from "../types/notificationTypes";
import { getNotificationFailureTypeName } from "../utils/getNotificationFailureTypeName";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import styles from "../../../AddEditAutoImportConfigurationsModal.module.css";
import { LFormInput, LFade, LDialog, LButton } from "@loffa/gpp-modules";
import { FormGrid } from "../../CommonComponents/FormGrid";
import { faExclamationTriangle } from "@fortawesome/free-solid-svg-icons";
import { useAutoImportFormContext } from "../../../../Context/AutoImportFormContext";
import { isOnFailureNotificationType } from "../utils/isOnFailureNotificationType";
import { LRequiredInput } from "../../CommonComponents/LRequiredInput";
import { KendoComboBoxEvent } from "@appTypes/kendoTypes";

const notificationTypeOptions = [
  { label: "On Errors/Exceptions", value: NotificationType.OnErrorsOrExceptions },
  { label: "On Success", value: NotificationType.OnSuccess },
  { label: "On Failure", value: NotificationType.OnFailure },
  { label: "On Process Completed", value: NotificationType.OnProcessCompleted },
];

const notificationFailureTypeOptions = [
  {
    label: getNotificationFailureTypeName(NotificationFailureType.IgnoreFileNotFoundError),
    value: NotificationFailureType.IgnoreFileNotFoundError,
  },
];

interface AddEditNotificationModalProps {
  isOpen: boolean;
  notification?: AutoImportNotification | null;
  onSave: (data: AutoImportNotification) => void;
  onClose: () => void;
}

const AddEditNotificationModal = ({isOpen, notification, onSave, onClose}: AddEditNotificationModalProps) => {
  const { setError, error } = useAutoImportFormContext();
  const [notificationType, setNotificationType] = useState<NotificationTypeOption>(notification?.notificationType ?? { 
    value: NotificationType.OnProcessCompleted, 
    label: "On Process Completed" 
  });
  const [typeSettings, setTypeSettings] = useState<NotificationFailureType[]>(notification?.typeSettings || []);
  const [to, setTo] = useState<string>(notification?.to || "");
  const [cc, setCc] = useState<string>(notification?.cc || "");
  const [bcc, setBcc] = useState<string>(notification?.bcc || "");
  const [supportEmail, setSupportEmail] = useState<string>(notification?.supportEmail || "");


  const handleNotificationTypeChange = (e: KendoComboBoxEvent) => {
    const selectedOption = e.target.value as NotificationTypeOption;
    setNotificationType(selectedOption);
    if (selectedOption && selectedOption?.value !== NotificationType.OnFailure) {
      setTypeSettings([]);//clears failure type if user switches "from On Failure"
    }
  };

  const handleToChange = (e: ChangeEvent<HTMLInputElement>) => {
    setTo(e.target.value);
  };

  const handleCcChange = (e: ChangeEvent<HTMLInputElement>) => {
    setCc(e.target.value);
  };

  const handleBccChange = (e: ChangeEvent<HTMLInputElement>) => {
    setBcc(e.target.value);
  };

  const handleSupportEmailChange = (e: ChangeEvent<HTMLInputElement>) => {
    setSupportEmail(e.target.value);
  };

  const handleFailureTypeChange = (type: NotificationFailureType,checked: boolean) => {
    if (checked) {
      setTypeSettings([...typeSettings, type]);
    } else {
      setTypeSettings(typeSettings.filter((t) => t !== type));
    }
  };

  const handleSave = (e: any) => {
    e.preventDefault();
    if (!notificationType) {
      setError("Please select a notification type.");
      return;
    }
    if (!to) {
      setError("Recipients (To) is required.");
      return;
    }
    //email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const emails = to.split(",").map((email) => email.trim());
    const invalidEmails = emails.filter((email) => !emailRegex.test(email));

    if (invalidEmails.length > 0) {
      setError(`Invalid email format: ${invalidEmails.join(", ")}`);
      return;
    }

    const newNotification: AutoImportNotification = {
      id: notification?.id || crypto.randomUUID(),
      notificationType,
      typeSettings: isOnFailureNotificationType(notificationType) ? typeSettings : [],
      to,
      cc,
      bcc,
      supportEmail,
      isActive: notification?.isActive ?? true,
    };
    onSave(newNotification);
  };

  const handleClose = () => {
    setNotificationType({ 
      value: NotificationType.OnProcessCompleted, 
      label: "On Process Completed" 
    });
    setTypeSettings([]);
    setTo("");
    setCc("");
    setBcc("");
    setSupportEmail("");
    setError("");
    onClose();
  };

  if (!isOpen) return null;

  const inputs = [
    {
      colSpan: 12,
      render: () => (
        <div className={styles.comboBoxLabelAlign}>
          <LRequiredInput
            type="combobox"
            labelName="Notification Type"
            labelAlign="top"
            required
            comboBox={{
              data: notificationTypeOptions,
              textField: "label",
              dataItemKey: "value",
              value: notificationType,
              onChange: handleNotificationTypeChange,
              placeholder: "-- Select Notification Type --",
            }}
          />
        </div>
      ),
    },
    ...(isOnFailureNotificationType(notificationType?.value)
      ? [
          {
            colSpan: 12,
            render: () => (
              <div className={styles.failureTypesContainer}>
                <label className={styles.failureTypesLabel}>
                  Notification Type Settings
                </label>
                <div className={styles.checkboxGroup}>
                  {notificationFailureTypeOptions?.map((option) => (
                    <div key={option.value} className={styles.checkboxInputAlign}>
                      <LFormInput
                        type="checkbox"
                        labelName={option.label}
                        checkbox={{
                          value: option.value,
                          checked: typeSettings.includes(option?.value),
                          onChange: (e: any) => handleFailureTypeChange(option.value, e.value),
                        }}
                      />
                    </div>
                  ))}
                </div>
              </div>
            ),
          },
        ]
      : []),
      {
        colSpan: 12,
        render: () => <NotificationTypeNote />,
      },
    {
      colSpan: 12,
      render: () => (
        <LRequiredInput
          type="input"
          labelAlign="top"
          labelName="To"
          required
          input={{
            name: "to",
            placeholder: "Enter Recipients (comma separated)",
            value: to,
            onChange: handleToChange,
          }}
        />
      ),
    },

    {
      colSpan: 6,
      render: () => (
        <LFormInput
          type="input"
          labelAlign="top"
          labelName="Cc"
          input={{
            name: "cc",
            placeholder: "Enter Cc Recipients (optional)",
            value: cc,
            onChange: handleCcChange,
          }}
        />
      ),
    },

    {
      colSpan: 6,
      render: () => (
        <LFormInput
          type="input"
          labelAlign="top"
          labelName="Bcc"
          input={{
            name: "bcc",
            placeholder: "Enter Bcc Recipients (optional)",
            value: bcc,
            onChange: handleBccChange,
          }}
        />
      ),
    },
    {
      colSpan: 12,
      render: () => (
        <LFormInput
          type="input"
          labelAlign="top"
          labelName="Support Email"
          input={{
            name: "supportEmail",
            placeholder: "Enter Support Email (Default: support@loffacorp.com)",
            value: supportEmail,
            onChange: handleSupportEmailChange,
          }}
        />
      ),
    },
  ];

  const modalBody = (
    <div className={styles.addEditNotificationModalContent}>
      {error && <div className={styles.errorMessage}>{error}</div>}
      <form onSubmit={handleSave} className={styles.addEditNotificationForm}>
        <FormGrid items={inputs} />
        <LButton type="submit" themeColor="primary">
          {notification ? "Save" : "Add"}
        </LButton>
      </form>
    </div>
  );

  return (
    <LFade transitionEnterDuration={300}>
      <LDialog
        width="900"
        key={isOpen}
        title={`${notification ? "Edit" : "Add"} Notification`}
        subtitle={<div className={styles.modalSubtitle}>Required Fields</div>}
        body={modalBody}
        actionCloseButton={handleClose}
        actionOverlay={handleClose}
      />
    </LFade>
  );
};

const NotificationTypeNote = () => {
  return (
    <div className={styles.deleteNote}>
      <FontAwesomeIcon
        icon={faExclamationTriangle}
        className={styles.warningIcon}
      />
      <span>
        <b>Note:</b> Available Options
        <ul>
          <li>
            <b>On Success</b> -&gt; Notification to Send When Process Completes with no Error nor Exceptions.
          </li>
          <li>
            <b>On Failure</b> -&gt; Notification to Send When Process Completes with Failures that are not file Exceptions. This notification will include a Log File Attached. Do not send to customers.
          </li>
          <li>
            <b>On Errors/Exceptions</b> -&gt; Notification to Send When Process Completes and File Contains Exceptions
          </li>
          <li>
            <b>On Process Completed</b> -&gt; Notification to Send When Process Completes Regardless the Result.
          </li>
        </ul>
      </span>
    </div>
  );
};

export { AddEditNotificationModal };
